import { Component, DestroyRef, OnDestroy, OnInit, signal } from '@angular/core';
import { SidebarConfig, SidebarItem, SidebarService } from './sidebar.service';
import { PalantirSharedModule } from '@shared/palantir-shared.module';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { collapsed } from '@core/animations/collapsed';
import { toggleFade } from '@core/animations/fadeIn';
import { AuthService } from '@core/services/auth/auth.service';
import { CdkMenuModule } from '@angular/cdk/menu';
import { ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MeGQL, MeQuery } from '@generated/graphql';
import { map } from 'rxjs';
import { QueryRef } from 'apollo-angular';
import { MySpaceBadgesService } from '@pages/my-space/my-space-badges.service';
import { FeatureFlagDirective } from '@shared/directives/feature-flag/feature-flag.directive';
import { HasPermissionDirective } from '@shared/directives/feature-flag/has-permission.directive';

@Component({
  selector: 'fx-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  providers: [SidebarService],
  imports: [
    PalantirSharedModule,
    CommonModule,
    RouterModule,
    CdkMenuModule,
    FeatureFlagDirective,
    HasPermissionDirective,
  ],
  animations: [collapsed, toggleFade],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit, OnDestroy {
  public user = signal<MeQuery['me'] | null>(null);
  public config: SidebarConfig = this.sidebarService.config();
  public isCollapsed = this.sidebarService.collapsed;
  public notificationsCount = signal(0);

  private queryRef: QueryRef<MeQuery>;

  constructor(
    public readonly sidebarService: SidebarService,
    private readonly authService: AuthService,
    private readonly destroyRef: DestroyRef,
    public readonly mySpaceBadgesService: MySpaceBadgesService,
    private readonly meGQL: MeGQL,
  ) {}

  ngOnInit() {
    this.sidebarService.notificationsCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.notificationsCount.set(value);
      });

    this._fetchUser();
  }

  ngOnDestroy(): void {
    if (this.queryRef) {
      this.queryRef.stopPolling();
    }
  }

  private _fetchUser() {
    this.queryRef = this.meGQL.watch();

    this.queryRef.valueChanges
      .pipe(
        map((result) => result.data.me),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        this.user.set(data);
      });
  }

  itemsAligned(direction: string): SidebarItem[] {
    return this.config.items.filter((item) => item.alignSelf === direction);
  }

  onLogout() {
    this.authService.logout();
  }
}
